import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { DateRangePicker } from 'react-dates';
import './notificationHistory.css';
import ReactLoading from 'react-loading';

import Pagination from '../../components/pagination/Pagination';
import { supNotificationHistory } from '../../api/supportAdmin';
import { ReactComponent as DateCalendar } from '../../assets/icons/trvto_dateCalendar.svg';

function NotificationHistory() {
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [focusedInput, setFocusedInput] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = useState({
        search: searchParams.get('search') || '',
        audience: searchParams.get('audience') || 'all',
        type: searchParams.get('type') || 'all',
        status: searchParams.get('status') || 'all',
        fromDate: searchParams.get('fromDate') || '',
        toDate: searchParams.get('toDate') || '',
        limit: Number(searchParams.get('limit')) || 10,
        offset: Number(searchParams.get('offset')) || 0
    });
    const pageNumber = Math.floor(filters.offset / filters.limit) + 1;

    const calendarIcon = (
        <span role='img' aria-label='calendar' className='calendar-icon'>
            <DateCalendar />
        </span>
    );

    useEffect(() => {
        if (filters.fromDate) {
            setStartDate(moment(filters.fromDate));
        }

        if (filters.toDate) {
            setEndDate(moment(filters.toDate));
        }
    }, []);

    const getNotificationHistory = async () => {
        try {
            setLoading(true);

            const response = await supNotificationHistory(filters);

            setNotifications(response.data.notifications);
            setTotalCount(response.data.total);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch whenever filters change
     */
    useEffect(() => {
        getNotificationHistory();
    }, [filters]);

    /**
     * Keep URL synced with filters
     */
    useEffect(() => {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== '' && value !== 'all' && value !== null && value !== undefined) {
                params.set(key, value);
            }
        });

        setSearchParams(params);
    }, [filters, setSearchParams]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;

        setFilters((prev) => ({
            ...prev,
            [name]: value,
            offset: 0
        }));
    };

    const handleSearch = () => {
        getNotificationHistory();
    };

    const refresh = () => {
        getNotificationHistory();
    };

    const clearFilters = () => {
        setStartDate(null);
        setEndDate(null);

        setFilters({
            search: '',
            audience: 'all',
            type: 'all',
            status: 'all',
            fromDate: '',
            toDate: '',
            limit: 10,
            offset: 0
        });
    };

    const paginationClick = (number, offSetNumber) => {
        setFilters((prev) => ({
            ...prev,
            offset: offSetNumber
        }));

        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };
    return (
        <div className='notification-history'>
            <div className='history-header'>
                <h2>Notification History</h2>

                <button className='refresh-btn' onClick={refresh}>
                    Refresh
                </button>
            </div>

            <div className='history-filters'>
                <input
                    type='text'
                    name='search'
                    placeholder='Search title...'
                    value={filters.search}
                    onChange={handleFilterChange}
                />

                <select name='audience' value={filters.audience} onChange={handleFilterChange}>
                    <option value='all'>All Recipients</option>
                    <option value='android'>Android</option>
                    <option value='ios'>iOS</option>
                </select>

                <select name='type' value={filters.type} onChange={handleFilterChange}>
                    <option value='all'>All Types</option>
                    <option value='OFFERS'>Offers</option>
                    <option value='TRIPS'>Trips</option>
                    <option value='BOOKINGS'>Bookings</option>
                    <option value='GENERAL'>General</option>
                </select>

                <select name='status' value={filters.status} onChange={handleFilterChange}>
                    <option value='all'>All Status</option>
                    <option value='success'>Success</option>
                    <option value='partial'>Partial</option>
                    <option value='failed'>Failed</option>
                </select>
                <DateRangePicker
                    startDate={startDate}
                    startDateId='notificationStartDate'
                    endDate={endDate}
                    endDateId='notificationEndDate'
                    focusedInput={focusedInput}
                    onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
                    onDatesChange={({ startDate, endDate }) => {
                        setStartDate(startDate);
                        setEndDate(endDate);

                        setFilters((prev) => ({
                            ...prev,
                            fromDate: startDate ? startDate.format('YYYY-MM-DD') : '',
                            toDate: endDate ? endDate.format('YYYY-MM-DD') : '',
                            offset: 0
                        }));
                    }}
                    startDatePlaceholderText='From'
                    endDatePlaceholderText='To'
                    displayFormat='DD/MM/YYYY'
                    noBorder
                    showDefaultInputIcon
                    customArrowIcon={calendarIcon}
                    orientation={isMobile ? 'vertical' : 'horizontal'}
                    isOutsideRange={() => false}
                />
                <button className='clear-btn' onClick={clearFilters}>
                    Clear
                </button>
            </div>

            {loading ? (
                <div className='loading-div'>
                    <ReactLoading type='spin' color='#2b7fff' height={40} width={40} />
                </div>
            ) : notifications.length === 0 ? (
                <div className='empty-history'>No Notifications Found</div>
            ) : (
                <>
                    <div className='history-table'>
                        <div className='notifi-heading'>Total {totalCount} Notifications</div>
                        <table>
                            <thead>
                                <tr>
                                    <th>Title</th>
                                    <th>Audience</th>
                                    <th>Type</th>
                                    <th>Success</th>
                                    <th>Failed</th>
                                    <th>Status</th>
                                    <th>Sent By</th>
                                    <th>Date</th>
                                    <th>View</th>
                                </tr>
                            </thead>

                            <tbody>
                                {notifications.map((row) => (
                                    <tr key={row._id}>
                                        <td>{row.title}</td>

                                        <td>
                                            {row.audience === 'all'
                                                ? 'All Users'
                                                : row.audience === 'android'
                                                  ? 'Android'
                                                  : 'iOS'}
                                        </td>

                                        <td>
                                            <span className='type-badge'>{row.type}</span>
                                        </td>

                                        <td>{row.successCount}</td>

                                        <td>{row.failureCount}</td>

                                        <td>
                                            <span className={`status-badge ${row.status || 'success'}`}>
                                                {row.status || 'Success'}
                                            </span>
                                        </td>

                                        <td>{row?.sentBy?.name}</td>

                                        <td>{new Date(row.createdAt).toLocaleString()}</td>

                                        <td>
                                            <button className='view-btn' onClick={() => setSelected(row)}>
                                                View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination
                        paginationClick={paginationClick}
                        numberOfRows={totalCount}
                        offSet={filters.offset}
                        pageNumber={pageNumber}
                        limit={filters.limit}
                    />
                    {selected && (
                        <div className='modal-overlay'>
                            <div className='notification-modal'>
                                <div className='modal-header'>
                                    <h3>Notification Details</h3>

                                    <button onClick={() => setSelected(null)}>✕</button>
                                </div>

                                <div className='modal-body'>
                                    <div className='detail-row'>
                                        <strong>Title</strong>

                                        <p>{selected.title}</p>
                                    </div>

                                    <div className='detail-row'>
                                        <strong>Message</strong>

                                        <p>{selected.body}</p>
                                    </div>

                                    <div className='detail-row'>
                                        <strong>Audience</strong>

                                        <p>{selected.audience}</p>
                                    </div>

                                    <div className='detail-row'>
                                        <strong>Type</strong>

                                        <p>{selected.type}</p>
                                    </div>

                                    <div className='detail-row'>
                                        <strong>Screen</strong>

                                        <p>{selected.screen}</p>
                                    </div>

                                    <div className='detail-row'>
                                        <strong>Total Users</strong>

                                        <p>{selected.totalUsers}</p>
                                    </div>

                                    <div className='detail-row'>
                                        <strong>Success</strong>

                                        <p>{selected.successCount}</p>
                                    </div>

                                    <div className='detail-row'>
                                        <strong>Failed</strong>

                                        <p>{selected.failureCount}</p>
                                    </div>

                                    <div className='detail-row'>
                                        <strong>Sent By</strong>

                                        <p>{selected?.sentBy?.name}</p>
                                    </div>

                                    <div className='detail-row'>
                                        <strong>Date</strong>

                                        <p>{new Date(selected.createdAt).toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default NotificationHistory;
